import React, { useEffect, useState } from "react";
import { useCompanion } from "@/companion/CompanionContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, boutiqueOf } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TotoAdaptiveDock } from "@/components/toto/TotoAdaptiveSurface";
import { toast } from "sonner";
import {
  Plus, Trash2, ChevronUp, ChevronDown, ArrowRightLeft, Save,
} from "lucide-react";
import "./floor-plan-space.css";
import "./floor-plan-horizontal.css";

/**
 * FloorPlanSettings — the once-per-boutique setup that Boutique Floor renders from.
 *
 * The user's mental model is spatial: every boutique has a hallway with rooms
 * on either side. This page lets a GM (or Rainbow leadership) describe that
 * layout literally — position, side, name, active. Everything else in
 * Toto that talks about rooms consumes this map.
 */

const BOUTIQUES = ["PLT", "FLL", "WYN", "PNC"];

export default function FloorPlanSettings() {
  const companion = useCompanion();
  const { user } = useAuth();
  const nav = useNavigate();
  const canPickBoutique = ["owner", "coo", "cfo", "leadership", "onboarding_admin", "district_leader"].includes(user?.role);
  const [searchParams] = useSearchParams();
  const requestedBoutique = searchParams.get("boutique")?.toUpperCase();
  const [boutique, setBoutique] = useState(() => canPickBoutique && BOUTIQUES.includes(requestedBoutique) ? requestedBoutique : user?.boutique_code || "PLT");
  const [plan, setPlan] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);


  useEffect(() => {
    let current = true;
    setPlan(null);
    setDirty(false);
    api.get(`/boutique-config/${boutique}/floor-plan`)
      .then((r) => { if (current) setPlan({ ...r.data, boutique_code: boutique }); })
      .catch(() => { if (current) setPlan(null); });
    return () => { current = false; };
  }, [boutique]);

  if (!plan) return <div className="px-10 py-10 text-stone-400 text-sm">Loading floor plan…</div>;

  const canEdit = !!plan.can_edit;
  const meta = boutiqueOf(boutique);

  // Group by side, preserve position order.
  const left  = plan.layout.filter((r) => r.side === "left").sort((a, b) => a.position - b.position);
  const right = plan.layout.filter((r) => r.side === "right").sort((a, b) => a.position - b.position);

  const patchRoom = (id, patch) => {
    setPlan((p) => ({ ...p, layout: p.layout.map((r) => r.id === id ? { ...r, ...patch } : r) }));
    setDirty(true);
  };

  const moveRoom = (id, dir) => {
    // Reorder within side.
    setPlan((p) => {
      const room = p.layout.find((r) => r.id === id);
      if (!room) return p;
      const sameSide = p.layout.filter((r) => r.side === room.side).sort((a, b) => a.position - b.position);
      const idx = sameSide.findIndex((r) => r.id === id);
      const target = dir === "up" ? idx - 1 : idx + 1;
      if (target < 0 || target >= sameSide.length) return p;
      // Swap positions with neighbor.
      const a = sameSide[idx], b = sameSide[target];
      return {
        ...p,
        layout: p.layout.map((r) => {
          if (r.id === a.id) return { ...r, position: b.position };
          if (r.id === b.id) return { ...r, position: a.position };
          return r;
        }),
      };
    });
    setDirty(true);
  };

  const flipSide = (id) => {
    setPlan((p) => {
      const room = p.layout.find((r) => r.id === id);
      const otherSide = room.side === "left" ? "right" : "left";
      const otherPositions = p.layout.filter((r) => r.side === otherSide).map((r) => r.position);
      const nextPos = (otherPositions.length ? Math.max(...otherPositions) : 0) + 1;
      return { ...p, layout: p.layout.map((r) => r.id === id ? { ...r, side: otherSide, position: nextPos } : r) };
    });
    setDirty(true);
  };

  const removeRoom = (id) => {
    if (!window.confirm("Remove this room from the floor plan?")) return;
    setPlan((p) => ({ ...p, layout: p.layout.filter((r) => r.id !== id) }));
    setDirty(true);
  };

  const addRoom = (side) => {
    const sameSide = plan.layout.filter((r) => r.side === side);
    const nextPos = (sameSide.map((r) => r.position).reduce((a, b) => Math.max(a, b), 0)) + 1;
    const total = plan.layout.length + 1;
    setPlan((p) => ({
      ...p,
      layout: [...p.layout, {
        id: `fr_new_${Math.random().toString(36).slice(2, 8)}`,
        position: nextPos, side, name: `Room ${total}`,
        type: "standard", active: true,
      }],
    }));
    setDirty(true);
  };

  const save = async () => {
    if (!canEdit || saving || plan.boutique_code !== boutique) return;
    setSaving(true);
    try {
      const r = await api.put(`/boutique-config/${plan.boutique_code}/floor-plan`, { layout: plan.layout });
      setPlan({ ...r.data, boutique_code: boutique });
      setDirty(false);
      toast.success("Floor plan saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the floor plan.");
    } finally {
      setSaving(false);
    }
  };

  return <main className="floor-plan-space" data-testid="floor-plan-settings">
    <section className="floor-plan-space__vessel" aria-labelledby="floor-plan-title">
      <header className="floor-plan-space__header"><div><h1 id="floor-plan-title">Floor Plan</h1></div></header>
      <section className="floor-plan-space__toolbar" aria-label="Floor plan controls"><div className="floor-plan-space__toolbar-actions">{canPickBoutique && <select aria-label="Boutique" value={boutique} disabled={saving} onChange={(e) => { if (!dirty || window.confirm("Leave without saving layout changes?")) setBoutique(e.target.value); }} data-testid="fp-boutique">{BOUTIQUES.map((b) => <option key={b} value={b}>{boutiqueOf(b).short}</option>)}</select>}{!canPickBoutique && <span className="floor-plan-space__boutique">{meta.short}</span>}{canEdit && <button type="button" onClick={save} disabled={!dirty || saving} data-testid="fp-save"><Save size={15} />{saving ? "Saving…" : dirty ? "Save changes" : "Saved"}</button>}</div></section>

        {/* Floor preview: left column · center hallway · right column. */}
        <section className="floor-plan-space__editor" data-testid="fp-editor"><div className="floor-plan-space__layout"><div className="floor-plan-space__side" style={{"--room-count": Math.max(1, left.length)}} aria-label="Top row">
              <div className="floor-plan-space__row-heading"><h2 className="floor-plan-row-label">Top Row</h2>
              {canEdit && (
                <button onClick={() => addRoom("left")} aria-label="Add room to top row" data-testid="fp-add-left" className="floor-plan-space__add">
                  <Plus size={12} strokeWidth={1.5} /> Add Room
                </button>
              )}
              </div>
              {left.map((r) => (
                <RoomEditor key={r.id} room={r} canEdit={canEdit} onPatch={patchRoom} onMove={moveRoom} onFlip={flipSide} onRemove={removeRoom} side="left" />
              ))}

            </div>
            <div className="floor-plan-space__hallway"><span>Hallway</span><i /></div><div className="floor-plan-space__side" style={{"--room-count": Math.max(1, right.length)}} aria-label="Bottom row">
              <div className="floor-plan-space__row-heading"><h2 className="floor-plan-row-label">Bottom Row</h2>
              {canEdit && (
                <button onClick={() => addRoom("right")} aria-label="Add room to bottom row" data-testid="fp-add-right" className="floor-plan-space__add">
                  <Plus size={12} strokeWidth={1.5} /> Add Room
                </button>
              )}
              </div>
              {right.map((r) => (
                <RoomEditor key={r.id} room={r} canEdit={canEdit} onPatch={patchRoom} onMove={moveRoom} onFlip={flipSide} onRemove={removeRoom} side="right" />
              ))}

            </div></div>
        </section>

        {!canEdit && (
          <p className="floor-plan-space__readonly">
            Read only — GMs, district leaders, and Rainbow leadership can edit the floor plan.
          </p>
        )}
    </section><TotoAdaptiveDock active="settings" />
  </main>;
}

function RoomEditor({ room, canEdit, onPatch, onMove, onFlip, onRemove, side }) {
  return (
    <div
      className={`floor-plan-space__room ${room.active ? "" : "is-inactive"}`}
      data-testid={`fp-room-${room.id}`}
    >
      <div className="floor-plan-space__room-head">
        <input
          value={room.name}
          onChange={(e) => onPatch(room.id, { name: e.target.value })}
          disabled={!canEdit}
          data-testid={`fp-name-${room.id}`}
          className="floor-plan-space__room-name"
          aria-label={`Name for ${room.name}`}
          placeholder="Room name"
        />

      </div>
      <div className="floor-plan-space__room-actions">
        <label>
          <input
            type="checkbox"
            checked={room.active}
            onChange={(e) => onPatch(room.id, { active: e.target.checked })}
            disabled={!canEdit}
            data-testid={`fp-active-${room.id}`}
          />
          Active
        </label>
        {canEdit && (
          <div>
            <button onClick={() => onMove(room.id, "up")} title="Move earlier in row" aria-label={`Move ${room.name} earlier in row`} data-testid={`fp-up-${room.id}`}>
              <ChevronUp size={13} strokeWidth={1.5} />
            </button>
            <button onClick={() => onMove(room.id, "down")} title="Move later in row" aria-label={`Move ${room.name} later in row`} data-testid={`fp-down-${room.id}`}>
              <ChevronDown size={13} strokeWidth={1.5} />
            </button>
            <button onClick={() => onFlip(room.id)} title={`Move to ${side === "left" ? "bottom" : "top"} row`} aria-label={`Move ${room.name} to ${side === "left" ? "bottom" : "top"} row`} data-testid={`fp-flip-${room.id}`}>
              <ArrowRightLeft size={12} strokeWidth={1.5} />
            </button>
            <button onClick={() => onRemove(room.id)} title="Remove" aria-label={`Remove ${room.name}`} data-testid={`fp-remove-${room.id}`}>
              <Trash2 size={12} strokeWidth={1.5} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
